import { wrapApiCall } from '@/lib/axios'
import { clampTotalPages, fetchPagesInBatches } from '@/utils/helper/paged-fetch.helper'
import type { AxiosInstance } from 'axios'

const base = '/postgrest'
const POSTGREST_PAGE_SIZE = 500
const IN_FILTER_CHUNK_SIZE = 200

const CREDIT_REPORT_SELECT = [
  'credit_tx_id',
  'sold_at',
  'sold_date_local',
  'member_id',
  'product_name',
  'sub_id',
  'earned_credits',
  'used_credits',
  'remaining_credits',
].join(',')

const CREDIT_CONVERTED_SELECT = [
  'credit_tx_id',
  'credit_used_at',
  'sold_at',
  'sold_date_local',
  'member_id',
  'product_name',
  'staff_name',
  'action_type',
  'revenue_type',
  'used_credits',
].join(',')

const CREDIT_PT_USAGE_SELECT = [
  'credit_tx_id',
  'credit_used_at',
  'sold_at',
  'sold_date_local',
  'member_id',
  'product_name',
  'staff_name',
  // QTY-2123: the Trainer dropdown matches rows to the trainer roster by id.
  'trainer_id',
  'action_type',
  'revenue_type',
  'used_credits',
].join(',')

const CREDIT_REMAINING_SNAPSHOT_SELECT = [
  'credit_tx_id',
  'sold_at',
  'sold_date_local',
  'member_id',
  'remaining_credits',
].join(',')

const USER_MEMBERSHIP_SELECT = ['user_id', 'package_id', 'start_date', 'expire_date'].join(',')
const PACKAGE_SELECT = ['id', 'package_type_id'].join(',')
const CREDIT_BALANCE_SELECT = ['user_id', 'total_credits'].join(',')

const toNumber = (value: unknown) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const toNullableString = (value: unknown) => (typeof value === 'string' ? value : null)

// A column the backend still reads through `pendingViewColumns` arrives as
// text, and as NULL from a tenant whose view has not been rebuilt yet.
const toNullableId = (value: unknown) => {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

const chunk = <T>(items: T[], size: number) => {
  const chunks: T[][] = []
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size))
  }
  return chunks
}

const uniqueNumbers = (values: number[]) =>
  Array.from(new Set(values.filter((value) => Number.isFinite(value))))

export type CreditReportFactRow = {
  creditTxId: number
  soldAt: string | null
  soldDateLocal: string | null
  memberId: number
  productName: string | null
  subId: string | null
  earnedCredits: number
  usedCredits: number
  remainingCredits: number
}

export type CreditConvertedFactRow = {
  creditTxId: number
  creditUsedAt: string | null
  soldAt: string | null
  soldDateLocal: string | null
  memberId: number
  productName: string | null
  staffName: string | null
  actionType: string | null
  revenueType: string | null
  credits: number
}

export type CreditPtUsageFactRow = {
  creditTxId: number
  creditUsedAt: string | null
  soldAt: string | null
  soldDateLocal: string | null
  memberId: number
  productName: string | null
  staffName: string | null
  trainerId: number | null
  actionType: string | null
  revenueType: string | null
  credits: number
}

export type MembershipWindow = {
  userId: number
  packageTypeId: number
  startDate: string | null
  expireDate: string | null
}

export type CreditBalanceRow = {
  userId: number
  totalCredits: number
}

export type CreditRemainingSnapshotRow = {
  creditTxId: number
  soldAt: string | null
  soldDateLocal: string | null
  memberId: number
  remainingCredits: number
}

export const CreditSummaryService = (client: AxiosInstance) => {
  const buildFetchAllQuery = ({
    select,
    page,
    order,
    filters,
    withCount,
  }: {
    select: string
    page: number
    order?: string
    filters?: string[]
    withCount: boolean
  }) => {
    const query = new URLSearchParams()
    query.append('select', select)
    if (withCount) query.append('count', 'true')
    query.append('page', String(page))
    query.append('pageSize', String(POSTGREST_PAGE_SIZE))
    if (order) query.append('order', order)
    ;(filters ?? []).forEach((filter) => query.append('filter', filter))
    return query
  }

  const fetchAllRows = async <T>({
    resource,
    select,
    order,
    filters,
  }: {
    resource: string
    select: string
    order?: string
    filters?: string[]
  }): Promise<T[]> => {
    const fetchPage = (page: number, withCount: boolean) =>
      wrapApiCall(() =>
        client.get<{
          data?: T[]
          pagination?: Record<string, unknown>
        }>(
          `${base}/${resource}?${buildFetchAllQuery({ select, page, order, filters, withCount }).toString()}`
        )
      )

    // Page 1 requests the count so we know the total up front; the remaining
    // pages skip `count=true` (the backend would otherwise re-run the full
    // view just to recompute an unchanging total) and are fetched in
    // parallel instead of sequentially, since the total is already known.
    const { data: firstData } = await fetchPage(1, true)
    const firstRows = firstData?.data ?? []

    const totalRaw = Number(firstData?.pagination?.total ?? firstRows.length)
    const total = Number.isFinite(totalRaw) ? totalRaw : firstRows.length
    const totalPages = clampTotalPages(total, POSTGREST_PAGE_SIZE, 'credit-summary.fetchAllRows')

    if (totalPages <= 1) {
      return firstRows
    }

    const restPages = await fetchPagesInBatches(
      Array.from({ length: totalPages - 1 }, (_, index) => index + 2),
      (page) => fetchPage(page, false).then(({ data }) => data?.data ?? [])
    )

    return [firstRows, ...restPages].flat()
  }

  const fetchRowsByInFilter = async <T>({
    resource,
    select,
    order,
    filterColumn,
    values,
    extraFilters,
  }: {
    resource: string
    select: string
    order?: string
    filterColumn: string
    values: number[]
    extraFilters?: string[]
  }): Promise<T[]> => {
    const ids = uniqueNumbers(values)
    if (!ids.length) return []

    const chunks = chunk(ids, IN_FILTER_CHUNK_SIZE)
    const chunkRows = await Promise.all(
      chunks.map((group) => {
        const filters = [`${filterColumn}=in.(${group.join(',')})`, ...(extraFilters ?? [])]
        return fetchAllRows<T>({ resource, select, order, filters })
      })
    )

    return chunkRows.flat()
  }

  return {
    async getCreditReportRowsByYear(year: number): Promise<CreditReportFactRow[]> {
      const yearFrom = `${year}-01-01`
      const yearTo = `${year + 1}-01-01`
      const rows = await fetchAllRows<Record<string, unknown>>({
        resource: 'credit_report_view',
        select: CREDIT_REPORT_SELECT,
        order: 'sold_at.desc,credit_tx_id.desc',
        filters: [`sold_date_local=gte.${yearFrom}`, `sold_date_local=lt.${yearTo}`],
      })

      return rows.map((row) => ({
        creditTxId: toNumber(row.credit_tx_id),
        soldAt: toNullableString(row.sold_at),
        soldDateLocal: toNullableString(row.sold_date_local),
        memberId: toNumber(row.member_id),
        productName: toNullableString(row.product_name),
        subId: toNullableString(row.sub_id),
        earnedCredits: toNumber(row.earned_credits),
        usedCredits: toNumber(row.used_credits),
        remainingCredits: toNumber(row.remaining_credits),
      }))
    },

    async getCreditConvertedRowsByYear(year: number): Promise<CreditConvertedFactRow[]> {
      const yearFrom = `${year}-01-01`
      const yearTo = `${year + 1}-01-01`
      const rows = await fetchAllRows<Record<string, unknown>>({
        resource: 'credit_converted_report_view',
        select: CREDIT_CONVERTED_SELECT,
        order: 'sold_at.desc,credit_tx_id.desc',
        filters: [`sold_date_local=gte.${yearFrom}`, `sold_date_local=lt.${yearTo}`],
      })

      return rows.map((row) => ({
        creditTxId: toNumber(row.credit_tx_id),
        creditUsedAt: toNullableString(row.credit_used_at),
        soldAt: toNullableString(row.sold_at),
        soldDateLocal: toNullableString(row.sold_date_local),
        memberId: toNumber(row.member_id),
        productName: toNullableString(row.product_name),
        staffName: toNullableString(row.staff_name),
        actionType: toNullableString(row.action_type),
        revenueType: toNullableString(row.revenue_type),
        credits: toNumber(row.used_credits),
      }))
    },

    async getCreditPtUsageRowsByYear(year: number): Promise<CreditPtUsageFactRow[]> {
      const yearFrom = `${year}-01-01`
      const yearTo = `${year + 1}-01-01`
      const rows = await fetchAllRows<Record<string, unknown>>({
        resource: 'credit_pt_usage_dashboard_view',
        select: CREDIT_PT_USAGE_SELECT,
        order: 'sold_at.desc,credit_tx_id.desc',
        filters: [`sold_date_local=gte.${yearFrom}`, `sold_date_local=lt.${yearTo}`],
      })

      return rows.map((row) => ({
        creditTxId: toNumber(row.credit_tx_id),
        creditUsedAt: toNullableString(row.credit_used_at),
        soldAt: toNullableString(row.sold_at),
        soldDateLocal: toNullableString(row.sold_date_local),
        memberId: toNumber(row.member_id),
        productName: toNullableString(row.product_name),
        staffName: toNullableString(row.staff_name),
        trainerId: toNullableId(row.trainer_id),
        actionType: toNullableString(row.action_type),
        revenueType: toNullableString(row.revenue_type),
        credits: toNumber(row.used_credits),
      }))
    },

    async getCreditRemainingSnapshotRowsThroughYear(
      year: number
    ): Promise<CreditRemainingSnapshotRow[]> {
      const yearTo = `${year + 1}-01-01`
      const rows = await fetchAllRows<Record<string, unknown>>({
        resource: 'credit_report_view',
        select: CREDIT_REMAINING_SNAPSHOT_SELECT,
        order: 'member_id.asc,sold_at.asc,credit_tx_id.asc',
        filters: [`sold_date_local=lt.${yearTo}`],
      })

      return rows.map((row) => ({
        creditTxId: toNumber(row.credit_tx_id),
        soldAt: toNullableString(row.sold_at),
        soldDateLocal: toNullableString(row.sold_date_local),
        memberId: toNumber(row.member_id),
        remainingCredits: toNumber(row.remaining_credits),
      }))
    },

    async getCreditBalances(): Promise<CreditBalanceRow[]> {
      const rows = await fetchAllRows<Record<string, unknown>>({
        resource: 'credit_balances',
        select: CREDIT_BALANCE_SELECT,
        order: 'user_id.asc',
      })

      return rows.map((row) => ({
        userId: toNumber(row.user_id),
        totalCredits: toNumber(row.total_credits),
      }))
    },

    async getMembershipWindowsByUserIds(userIds: number[]): Promise<MembershipWindow[]> {
      const memberships = await fetchRowsByInFilter<Record<string, unknown>>({
        resource: 'user_memberships',
        select: USER_MEMBERSHIP_SELECT,
        order: 'user_id.asc,start_date.asc',
        filterColumn: 'user_id',
        values: userIds,
      })

      const packageIds = uniqueNumbers(memberships.map((row) => toNumber(row.package_id)))
      const packages = await fetchRowsByInFilter<Record<string, unknown>>({
        resource: 'packages',
        select: PACKAGE_SELECT,
        order: 'id.asc',
        filterColumn: 'id',
        values: packageIds,
      })

      const packageTypeByPackageId = new Map<number, number>()
      packages.forEach((row) => {
        packageTypeByPackageId.set(toNumber(row.id), toNumber(row.package_type_id))
      })

      return memberships
        .map((row) => {
          const packageId = toNumber(row.package_id)
          const packageTypeId = packageTypeByPackageId.get(packageId)
          if (packageTypeId === undefined) return null

          return {
            userId: toNumber(row.user_id),
            packageTypeId,
            startDate: toNullableString(row.start_date),
            expireDate: toNullableString(row.expire_date),
          } satisfies MembershipWindow
        })
        .filter((row): row is MembershipWindow => !!row)
    },
  }
}
